import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setDropdownOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const links = [
    { label: "Home", href: "/" },
    { label: "Scripture", href: "/scripture" },
    { label: "Devotionals", href: "/devotionals" },
  ];

  const userLinks = [
    { label: "Reading Plan", href: "/scripture", icon: "ri-calendar-check-line" },
    { label: "Highlighted Verses", href: "/favorites", icon: "ri-bookmark-line" },
    { label: "My Notes", href: "/notes", icon: "ri-file-text-line" },
    { label: "Devotionals", href: "/devotionals", icon: "ri-sun-line" },
    { label: "Settings", href: "/settings", icon: "ri-settings-3-line" },
    { label: "Sync to New Device", href: "/login", icon: "ri-smartphone-line" },
  ];

  const displayName = user?.name || "My Account";
  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : null;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0a0a0a] border-b border-[#1e1e1e]" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://storage.readdy-site.link/project_files/643939e0-73b1-412d-a6cb-581a1ba0e14c/d7ca5519-1c4a-4f93-9ca8-2ced6526acdd_bible-image.jpg?v=5a2efdf43c26bdf7c8a26d76507b46fb"
            alt="Bible"
            className="w-7 h-7 object-cover rounded-md"
          />
          <span className="text-white font-semibold text-sm tracking-wide">AppleSMP Bible</span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              className={`text-sm px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === l.href
                  ? "text-white bg-[#1a1a1a]"
                  : "text-[#888] hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        {/* Right — always show account dropdown */}
        <div className="hidden md:flex items-center gap-2">
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#222] border border-[#2e2e2e] text-white text-sm px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-6 h-6 flex-shrink-0 rounded-full overflow-hidden">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                ) : initials ? (
                  <div className="w-full h-full flex items-center justify-center bg-[#ef4444]">
                    <span className="text-white text-xs font-semibold leading-none">{initials}</span>
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[#1e1e1e]">
                    <i className="ri-user-line text-[#666] text-xs" />
                  </div>
                )}
              </div>
              <span className="max-w-[120px] truncate">{displayName}</span>
              {dropdownOpen
                ? <i className="ri-arrow-up-s-line text-[#555] text-xs" />
                : <i className="ri-arrow-down-s-line text-[#555] text-xs" />
              }
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-[#111] border border-[#1e1e1e] rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-[#1e1e1e]">
                  <p className="text-white text-sm font-medium truncate">{displayName}</p>
                  <p className="text-[#444] text-xs truncate mt-0.5">
                    {user?.isAnonymous ? "No account needed" : user?.email}
                  </p>
                </div>
                <div className="py-1.5">
                  {userLinks.map((l) => (
                    <Link
                      key={l.href + l.label}
                      to={l.href}
                      className="flex items-center gap-2.5 px-4 py-2 text-[#888] hover:text-white hover:bg-[#1a1a1a] text-sm transition-colors cursor-pointer"
                    >
                      <i className={`${l.icon} text-sm w-4 h-4 flex items-center justify-center`} />
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-[#888] hover:text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0a0a0a] border-b border-[#1e1e1e] px-4 pb-4">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.href}
              onClick={() => setMenuOpen(false)}
              className={`block text-sm py-2.5 border-b border-[#1a1a1a] last:border-0 cursor-pointer ${
                location.pathname === l.href ? "text-white" : "text-[#888] hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="mt-3 pt-3 border-t border-[#1a1a1a] space-y-1">
            {userLinks.map((l) => (
              <Link
                key={l.href + l.label}
                to={l.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 text-[#888] hover:text-white text-sm py-2 cursor-pointer transition-colors"
              >
                <i className={`${l.icon} text-sm`} />
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
